<script setup lang="ts">
import type { Schemas } from "#shopware";

const props = defineProps<{
    navigationId: string;
}>();

const { search } = useCategorySearch();
const { t } = useI18n();
const { metaKey } = useThemeMeta();
const { buildQuery } = useCustomQuery();

const route = useRoute();
const { data: categoryResponse } = await useAsyncData(
    "cmsNavigation" + props.navigationId,
    async () => {
        return await search(props.navigationId, {
            withCmsAssociations: true,
            query: buildQuery(),
        });
    },
);

const { category } = useCategory(categoryResponse as Ref<Schemas["Category"]>);

// Build meta title
const metaTitle = computed(() => {
    const categoryName = category.value?.translated?.name || category.value?.name || ''

    if (category.value?.translated?.metaTitle) {
        return category.value.translated.metaTitle
    } else if (categoryName) {
        return t(`${metaKey}.category.title`, { categoryName })
    } else {
        return t(`${metaKey}.category.fallbackTitle`)
    }
})

// Build meta description
const metaDescription = computed(() => {
    const categoryName = (category.value?.customFields as Record<string, string | undefined>)?.category_seo_h1_tag || category.value?.translated?.name || category.value?.name || ''

    if (category.value?.translated?.metaDescription) {
        return category.value.translated.metaDescription
    } else if (categoryName) {
        return t(`${metaKey}.category.metaDescription`, { categoryName })
    } else {
        return t(`${metaKey}.category.metaDescriptionFallback`)
    }
})

const robotsContent = computed(() =>
    Object.keys(route.query).length > 0 ? 'noindex, follow' : 'index, follow',
)

useHead({
    title: metaTitle,
    meta: [
        { name: 'description', content: metaDescription },
        { name: 'og:description', content: metaDescription },
        { name: 'robots', content: robotsContent, tagPriority: 'high' },
    ],
})
</script>

<template>
    <StructurePage v-if="category.cmsPage" :content="category.cmsPage" />
</template>
